import createClient from 'openapi-fetch';
import type { components, paths } from './schema';

export type Schemas = components['schemas'];
export const API_BASE = '/api/v1';

/** Error body of every non-2xx response (API/CONVENTIONS.md). */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: { path: string; message: string }[];
    requestId?: string;
  };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: { path: string; message: string }[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static async from(response: Response): Promise<ApiError> {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
    return new ApiError(
      response.status,
      body?.error.code ?? `HTTP_${String(response.status)}`,
      body?.error.message ?? response.statusText,
      body?.error.details ?? [],
      body?.error.requestId ?? response.headers.get('x-request-id') ?? undefined,
    );
  }
}

// --- session plumbing (set by the auth provider) ---------------------------------------

interface SessionHooks {
  accessToken: () => string | null;
  /** Gets a new access token with the refresh token; null when the session is over. */
  refresh: () => Promise<string | null>;
  onSessionExpired: () => void;
}

let hooks: SessionHooks = {
  accessToken: () => null,
  refresh: () => Promise.resolve(null),
  onSessionExpired: () => undefined,
};

export function bindSession(next: SessionHooks): void {
  hooks = next;
}

const lockListeners = new Set<() => void>();

/** Called whenever the API answers 402 SUBSCRIPTION_LOCKED (the shop just locked). */
export function onSubscriptionLocked(listener: () => void): () => void {
  lockListeners.add(listener);
  return () => lockListeners.delete(listener);
}

function noticeLock(response: Response): void {
  if (response.status !== 402 || lockListeners.size === 0) return;
  void response
    .clone()
    .json()
    .then((b: ApiErrorBody | null) => {
      if (b?.error.code === 'SUBSCRIPTION_LOCKED') lockListeners.forEach((l) => l());
    })
    .catch(() => undefined);
}

let refreshing: Promise<string | null> | null = null;

/** Runs one refresh at a time; concurrent 401s share it. */
function refreshOnce(): Promise<string | null> {
  refreshing ??= hooks.refresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function authFetch(input: Request): Promise<Response> {
  const retry = input.clone();
  const send = (req: Request, token: string | null) => {
    if (token) req.headers.set('Authorization', `Bearer ${token}`);
    if (req.method === 'POST' && !req.headers.has('Idempotency-Key')) {
      req.headers.set('Idempotency-Key', crypto.randomUUID());
    }
    return fetch(req);
  };
  const response = await send(input, hooks.accessToken());
  noticeLock(response);
  if (response.status !== 401 || input.url.includes('/auth/')) return response;
  const token = await refreshOnce();
  if (!token) {
    hooks.onSessionExpired();
    return response;
  }
  const retried = await send(retry, token);
  noticeLock(retried);
  return retried;
}

export const api = createClient<paths>({
  baseUrl: new URL(API_BASE, globalThis.location.origin).href,
  fetch: authFetch,
});

/** GETs an image URL the API handed out (`imageUrl`, `logoUrl`) as a blob, with the session token. */
export async function fetchBlob(url: string): Promise<Blob> {
  const response = await authFetch(new Request(new URL(url, globalThis.location.origin)));
  if (!response.ok) throw await ApiError.from(response);
  return response.blob();
}

/** Unwraps an openapi-fetch result: returns data or throws ApiError. */
export async function unwrap<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise;
  if (error !== undefined || !response.ok) {
    const body = error as ApiErrorBody | undefined;
    throw new ApiError(
      response.status,
      body?.error.code ?? `HTTP_${String(response.status)}`,
      body?.error.message ?? response.statusText,
      body?.error.details ?? [],
      body?.error.requestId ?? response.headers.get('x-request-id') ?? undefined,
    );
  }
  return data as T;
}

/** For endpoints that can also answer CSV: unwraps the JSON form. */
export async function unwrapJson<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<Exclude<T, string>> {
  const data = await unwrap(promise);
  if (typeof data === 'string') throw new ApiError(500, 'UNEXPECTED_CSV', 'Expected JSON');
  return data as Exclude<T, string>;
}
